import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  ArrowRight, 
  Crown,
  CheckCircle2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import deliMesa from '../../assets/images/deli-mesa.png';
import { RESERVATION_TYPES } from '../../data/reservationData'; // <-- IMPORTANDO A CONSTANTE

// --- Shared UI Components ---

const SectionSeparator = () => (
  <div className="flex justify-center items-center py-16 opacity-40">
    <div className="h-[1px] w-24 bg-gradient-to-r from-transparent to-[#971253]"></div>
    <div className="mx-6 text-[#971253]">
      <Crown size={20} strokeWidth={1} />
    </div>
    <div className="h-[1px] w-24 bg-gradient-to-l from-transparent to-[#971253]"></div>
  </div>
);

// Triplicamos a array importada para criar o efeito de loop infinito perfeitamente
const extendedTypes = [...RESERVATION_TYPES, ...RESERVATION_TYPES, ...RESERVATION_TYPES];

// --- Main Page Component ---

const ReservationsPage = () => {
  const carouselRef = useRef(null);
  const scrollTimeout = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const navigate = useNavigate();
  const handleReservationClick = (type) => {
    navigate("/reserva", {
        state: {
          prefillData: { reservationType: type, numPeople: 4 } 
        }
    });
  };

  useEffect(() => {
    if (carouselRef.current) {
      setTimeout(() => {
        if (carouselRef.current && carouselRef.current.children[0]) {
          const cardWidth = carouselRef.current.children[0].offsetWidth;
          const gap = 24; 
          const blockWidth = (cardWidth + gap) * RESERVATION_TYPES.length;
          
          carouselRef.current.scrollLeft = blockWidth;
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const intervalId = setInterval(() => {
      if (carouselRef.current && carouselRef.current.children[0]) {
        const cardWidth = carouselRef.current.children[0].offsetWidth;
        const gap = 24;
        carouselRef.current.scrollBy({ left: cardWidth + gap, behavior: 'smooth' });
      }
    }, 4000); 

    return () => clearInterval(intervalId);
  }, [isPaused]);

  const handleScroll = (e) => {
    const container = e.target;
    
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      if (!carouselRef.current || !carouselRef.current.children[0]) return;
      
      const cardWidth = carouselRef.current.children[0].offsetWidth;
      const gap = 24; 
      const blockWidth = (cardWidth + gap) * RESERVATION_TYPES.length;
      
      if (container.scrollLeft >= blockWidth * 2 - (cardWidth / 2)) {
        container.scrollLeft -= blockWidth;
      } 
      else if (container.scrollLeft < blockWidth - (cardWidth / 2)) {
        container.scrollLeft += blockWidth;
      }
    }, 150); 
  };

  const scroll = (direction) => {
    if (carouselRef.current && carouselRef.current.children[0]) {
      const cardWidth = carouselRef.current.children[0].offsetWidth;
      const gap = 24;
      const scrollAmount = cardWidth + gap;
      carouselRef.current.scrollBy({ 
        left: direction === 'left' ? -scrollAmount : scrollAmount, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF5EE] text-stone-800 font-sans selection:bg-[#971253] selection:text-white overflow-x-hidden">

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* 1. HERO SECTION */}
      <section className="relative h-screen flex flex-col items-center justify-center px-6 text-center bg-[#528a89] overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 border-r border-b border-[#FFF5EE]/20 rounded-br-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border-l border-t border-[#FFF5EE]/20 rounded-tl-[100px] pointer-events-none" />
        
        <div className="max-w-4xl z-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
          <span className="flex items-center justify-center gap-2 text-[#FFF5EE] uppercase tracking-[0.2em] text-sm font-medium mb-8">
            <Sparkles size={16} /> Planeje sua Visita
          </span>
          
          <h1 className="font-serif text-6xl md:text-8xl text-[#FFF5EE] leading-[1.1] mb-10 [text-shadow:4px_4px_0px_#234e4d]">
            Reserve Sua <br/> 
            <span className="italic text-5xl md:text-7xl opacity-90 block mt-2">
              Experiência
            </span>
          </h1>
          
          <p className="max-w-lg mx-auto text-lg text-[#FFF5EE] leading-relaxed mb-12 font-light drop-shadow-md">
            De encontros casuais com jogos de tabuleiro e campanhas épicas de RPG à comemorações inesquecíveis. O seu lugar está guardado.
          </p>

          <button onClick={() => document.getElementById('reservas').scrollIntoView({ behavior: 'smooth' })} className="group relative px-10 py-5 bg-[#971253] text-[#FFF5EE] rounded-full overflow-hidden shadow-[0_10px_20px_-5px_rgba(0,0,0,0.3)] hover:shadow-xl hover:shadow-[#971253]/40 transition-all border border-[#FFF5EE]/30">
            <span className="relative z-10 flex items-center font-medium tracking-wide text-lg">
              Ver Tipos de Reserva <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-500" />
            </span>
            <div className="absolute inset-0 bg-[#7a0e42] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-out" />
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full h-auto block align-middle">
              <path fill="#FFF5EE" fillOpacity="1" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
           </svg>
        </div>
      </section>

      {/* 2. RESERVATION TYPES CAROUSEL */}
      <section id="reservas" className="py-24 px-6 relative">
        <div 
          className="max-w-7xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#971253] mb-4">Escolha Sua Aventura</h2>
              <p className="text-stone-500 text-lg">Deslize para ver todas as opções de reservas disponíveis.</p>
            </div>
            
            <div className="hidden md:flex gap-4">
              <button onClick={() => scroll('left')} className="p-4 rounded-full border border-[#528a89]/30 text-[#528a89] hover:bg-[#528a89] hover:text-[#FFF5EE] transition-colors focus:outline-none">
                <ChevronLeft size={24} />
              </button>
              <button onClick={() => scroll('right')} className="p-4 rounded-full border border-[#528a89]/30 text-[#528a89] hover:bg-[#528a89] hover:text-[#FFF5EE] transition-colors focus:outline-none">
                <ChevronRight size={24} />
              </button>
            </div>
          </div>

          <div 
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-6 pb-12 -mx-6 px-6 md:mx-0 md:px-0"
          >
            {extendedTypes.map((type, index) => (
              <div 
                key={`${type.id}-${index}`}
                className="relative snap-center shrink-0 w-[85vw] md:w-[400px] bg-white/60 backdrop-blur-sm border border-white/50 rounded-[2rem] p-8 shadow-sm hover:shadow-xl transition-all duration-500 group flex flex-col"
              >
                <div className={`absolute top-0 left-0 w-full h-2 rounded-t-[2rem] ${type.color === 'wine' ? 'bg-[#971253]' : 'bg-[#528a89]'}`} />
                
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 relative
                  ${type.color === 'wine' ? 'bg-[#971253]/10 text-[#971253]' : 'bg-[#528a89]/10 text-[#528a89]'}
                `}>
                  <type.icon size={32} strokeWidth={1.5} className="group-hover:scale-110 transition-transform duration-500" />
                  <div className={`absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 blur-md transition-opacity duration-500 ${type.color === 'wine' ? 'bg-[#971253]' : 'bg-[#528a89]'}`} />
                </div>

                <h3 className={`font-serif text-3xl mb-2 ${type.color === 'wine' ? 'text-[#971253]' : 'text-[#528a89]'}`}>
                  {type.title}
                </h3>
                <span className="text-sm font-medium tracking-wide uppercase text-stone-400 mb-4 block">
                  {type.subtitle}
                </span>
                
                <p className="text-stone-600 leading-relaxed mb-8 flex-grow">
                  {type.description}
                </p>

                <ul className="space-y-4 mb-10">
                  {type.benefits.map((benefit, bIdx) => (
                    <li key={bIdx} className="flex items-start gap-3 text-stone-600">
                      <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${type.color === 'wine' ? 'text-[#971253]/70' : 'text-[#528a89]/70'}`} />
                      <span className="text-sm leading-tight">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => handleReservationClick(type.id)} 
                  className={`
                  w-full py-4 rounded-xl font-medium tracking-wide transition-all mt-auto border-2
                  ${type.color === 'wine' 
                    ? 'bg-[#971253] text-[#FFF5EE] border-[#971253] hover:bg-transparent hover:text-[#971253]' 
                    : 'bg-[#528a89] text-[#FFF5EE] border-[#528a89] hover:bg-transparent hover:text-[#528a89]'
                  }
                `}>
                  Reservar
                </button>
              </div>
            ))}
          </div>

          <div className="flex md:hidden justify-center items-center gap-2 text-stone-400 text-sm mt-4">
            <ArrowRight size={16} className="animate-pulse" />
            <span>Deslize para ver mais</span>
          </div>

        </div>
      </section>

      {/* 3. EXPERIENCE SECTION (Editorial Block) */}
      <section className="py-24 px-6 bg-white border-y border-[#971253]/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          
          <div className="relative aspect-square md:aspect-[4/5] rounded-tl-[100px] rounded-br-[100px] bg-stone-200 overflow-hidden shadow-2xl">
            <img 
              src={deliMesa}
              alt="Mesa jogando na Deli da Persy" 
              className="object-cover w-full h-full hover:scale-105 transition-transform duration-1000"
            />
            <div className="absolute -z-10 top-6 left-6 w-full h-full border border-[#528a89]/50 rounded-tl-[100px] rounded-br-[100px]" />
          </div>

          <div className="space-y-8">
            <h2 className="font-serif text-4xl md:text-5xl text-[#971253]">A Mesa é o Centro de Tudo</h2>
            <div className="prose prose-lg text-stone-600">
              <p className='text-justify text-base/6'>
                Acreditamos que as melhores conexões acontecem quando olhamos nos olhos uns dos outros, rimos juntos e compartilhamos um desafio na mesa.
              </p>
              <br></br>
              <p className='text-justify text-base/6'>
                Nossa equipe é apaixonada por encontrar o jogo perfeito para o seu grupo. Seja sua primeira vez rolando um dado ou seja você um estrategista veterano, criamos um ambiente acolhedor, onde as regras são explicadas com paciência e o atendimento é personalizado.
              </p>
            </div>
          </div>

        </div>
      </section>

      <SectionSeparator />

      {/* 4. FINAL CTA */}
      <section className="py-20 px-6 text-center relative overflow-hidden">
        <div className="max-w-2xl mx-auto relative z-10">
          <h2 className="font-serif text-5xl md:text-7xl text-[#971253] mb-8">Pronto para Reservar Sua Mesa?</h2>
          <p className="text-stone-600 text-lg mb-10">
            Nossos espaços são limitados para garantir a melhor experiência acústica e imersiva para o seu grupo. Garanta sua aventura.
          </p>
          <button 
            onClick={() => handleReservationClick('boardgame')} 
            className="px-12 py-5 bg-[#528a89] text-white rounded-2xl text-lg font-medium tracking-wide hover:bg-[#3f6d6c] hover:scale-105 transition-all shadow-lg hover:shadow-[#528a89]/30 border-b-4 border-[#3a6362] active:border-b-0 active:translate-y-1"
          >
            Fazer Reserva Agora
          </button>
        </div>
        
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#528a89]/5 rounded-full z-0 pointer-events-none" />
      </section>

    </div>
  );
};

export default ReservationsPage;